/**
 * FACT-CHECK CLASSIFICATION — is a sentence a legal rule, or a happening?
 *
 * The claim check never lets a model decide what is a claim. This is the code that decides which side of one
 * line a sentence is on: a sentence that STATES a duty, a threshold, a deadline, a duration or an eligibility
 * rule is a legal claim, whether or not it cites a section; a sentence that REPORTS a date or a happening
 * ("On September 21 the Governor wrote to TCEQ", "the committee held a hearing") is an event and is never
 * judged against a statute.
 *
 * It first shipped as one cue list, and it was too eager in one direction (news about an "exemption" was a
 * duty) and too stingy in the other (a rule about a rendition, a lien, a county's job requirement or the
 * size of a qualifying data center had no cue at all, so it was an event and skipped). The 2026-10-05 ITC
 * articles supplied the real sentences on both sides; they are the tests.
 *
 * Pure: no I/O, no model, no clock.
 */

/**
 * What makes a sentence a LEGAL rule rather than a statement of a date. A deadline claim needs a duty, not
 * just a date: "A second question arrives on January 1, 2027" and "the Governor wrote to TCEQ on September
 * 21" carry dates and no duty. A strong word of tax procedure is enough; a bare modal ("must", "requires")
 * counts only beside the vocabulary of tax law, so "the letter requires TCEQ to update the Governor's
 * office by October 19" (an order, not a statute) does not.
 */
const LEGAL_STRONG_CUE =
  /\b(?:deadlines?|penalt(?:y|ies)|delinquen\w*|due\s+(?:by|on|before|no later than|within|date)|extend(?:s|ed)?|extensions?|protest\w*|appeal\w*|exempt\w*|abate\w*|burden of proof|interest\s+(?:accrues?|rate|charges?)|tax rate|filing|filed|files?\s+(?:a|an|the|with))\b/i;
const LEGAL_MODAL_CUE = /\b(?:must|shall|required?|requires?|may not|entitled|obligat\w+)\b/i;
/** The vocabulary of tax law. On its own it makes a sentence about the law, not a duty. */
const LEGAL_DOMAIN =
  /\b(?:tax(?:es|able|payers?)?|apprais\w+|propert(?:y|ies)|assess\w*|valu(?:e|ation)|owners?|statut\w*|law|code|section|chapter)\b/i;

/** Quoted text is somebody else's words: a "must" inside a quotation is not this sentence's rule. */
export function withoutQuotes(sentence: string): string {
  return sentence.replace(/"[^"]*"/g, ' ').replace(/\s+/g, ' ');
}

export function hasLegalDutyCue(sentence: string): boolean {
  return (
    LEGAL_STRONG_CUE.test(sentence) ||
    (LEGAL_MODAL_CUE.test(sentence) && LEGAL_DOMAIN.test(sentence))
  );
}

/** Speaks the language of tax law: enough to take the provision its own paragraph cites, not enough to stand alone. */
export function speaksOfTheLaw(sentence: string): boolean {
  return hasLegalDutyCue(sentence) || LEGAL_DOMAIN.test(sentence);
}

const NUMBER_WORD =
  '(?:\\d|(?:one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|fifteen|twenty|thirty|forty|fifty|sixty|ninety|hundred|thousand)\\b)';
/** "at least 20 jobs", "no more than ten years", "up to 100 percent", "a minimum of $200 million". */
export const THRESHOLD = new RegExp(
  `\\b(?:at least|at most|no (?:more|less|fewer|greater) than|not (?:more|less|fewer|greater) than|up to|a minimum of|a maximum of|minimum of|maximum of|capped at|exceeds?)\\s+(?:of\\s+)?(?:\\$\\s?)?${NUMBER_WORD}`,
  'i',
);
/** What a threshold is a threshold OF: a programme's size, term or rate, never a head count in a news item. */
const PROGRAM_NOUN =
  /\b(?:jobs?|full-time|square (?:feet|foot)|megawatts?|MW|capital investment|investments?|abatements?|exemptions?|tax(?:es)?|wages?|years?|months?|days?|percent|%|value|employees?)\b/i;
const DURATION_RULE =
  /\b(?:remains?|stays?|continues?|are|is)\s+(?:in effect|valid|effective)\b|\bin effect for\b|\b(?:expires?|expiring|expiration)\b/i;
const ELIGIBILITY = /\b(?:qualif(?:y|ies|ied|ying)|eligib\w+|requirements?|criteria|threshold)\b/i;
/**
 * The mechanics of Texas property-tax procedure. A dated or numbered sentence that names one of them states
 * how the process works ("April 15 for the rendition, May 15 on written request"; "the property tax lien and
 * the rendition duty all key off the same date"), not what happened on a day.
 */
const TAX_PROCESS =
  /\b(?:rendition (?:duty|deadline|statements?)|duty to render|tax liens?|lien attaches|written request|extension|deadline|due date|appraisal date|key(?:s)? off)\b/i;
/** A legislative calendar is a list of dates, not a tax rule. */
const NEWS_CALENDAR =
  /\b(?:bill filing|file bills|session convenes|convenes|legislative session|regular session|legislators?)\b/i;
/** A verb that reports what somebody did or said. */
const REPORTING_VERB =
  /\b(?:held|heard|wrote|directed|ordered|announced|released|reports?|reported|testified|voted|approved|adopted|paused|dropped|told|said|stated|received|issued|sent|signed|opened|convened|requested|proposed|described|explained|noted|added|began|started|resumed|expects?|expected|crowded|attended)\b/i;

export function isQuestion(sentence: string): boolean {
  return /\?["”')\]]*\s*$/.test(sentence.trim());
}

/** Reports what happened or what somebody said, rather than stating a rule. */
export function isReportedHappening(sentence: string): boolean {
  return REPORTING_VERB.test(withoutQuotes(sentence));
}

/**
 * The sentence states a duty, a threshold, a deadline, a duration or an eligibility rule, in its OWN words.
 * A question, a legislative calendar and a report of what somebody did or said are not that. A duration
 * ("they remain in effect for two years") and an obligation beside tax vocabulary state a rule even in
 * a sentence that also reports one.
 */
export function statesLegalRule(sentence: string): boolean {
  return ruleKind(sentence) !== null;
}

/**
 * A rule worth taking a citation from the other end of a section: a duration, an obligation beside tax
 * vocabulary, a programme threshold, an eligibility test or a named step of tax procedure. A bare word like
 * "abatement" or "filing" makes a sentence a legal statement, not one whose provision is whatever happens to be cited
 * nearby.
 */
export function statesStrictLegalRule(sentence: string): boolean {
  return ruleKind(sentence) === 'strict';
}

function ruleKind(sentence: string): 'strict' | 'lexical' | null {
  if (isQuestion(sentence)) return null;
  const own = withoutQuotes(sentence);
  if (NEWS_CALENDAR.test(own)) return null;
  if (DURATION_RULE.test(own) || (LEGAL_MODAL_CUE.test(own) && LEGAL_DOMAIN.test(own)))
    return 'strict';
  if (REPORTING_VERB.test(own)) return null;
  if (
    TAX_PROCESS.test(own) ||
    ELIGIBILITY.test(own) ||
    (THRESHOLD.test(own) && PROGRAM_NOUN.test(own))
  )
    return 'strict';
  return hasLegalDutyCue(own) ? 'lexical' : null;
}
